import uid from "common/utils/uid";
import type { DocMigration } from "../docMigration";

// Copied from lib/house/plans/drawingTools.ts
const STATIC_MISSING_DRAWING_IMG_PATH = `/static/plans/details/detail_missing.png`;

const foundationHashes = [
  "SLAB_ON_GRADE.MONOLITHIC",
  "SLAB_ON_GRADE.STEM_WALL_CMU",
  "SLAB_ON_GRADE.STEM_WALL_CIP",
  "RAISED_SLAB.STEM_WALL_CMU",
  "RAISED_SLAB.STEM_WALL_CIP",
  "CRAWLSPACE.STEM_WALL_CMU",
  "CRAWLSPACE.STEM_WALL_CIP",
  "BASEMENT.STEM_WALL_CMU",
  "BASEMENT.STEM_WALL_CIP",
];

// eslint-disable-next-line @typescript-eslint/naming-convention
const migration_0_0_145: DocMigration = {
  maxSchemaVersion: "0.0.145",
  description: "Move statically-created drawings to new drawing catalog",
  apply(json: any) {
    // Find & store any previous settings sets ids w/ detail catalog setting ids for later reference
    const settingsSetIdsToDrawingCatalogId = {};

    if (json.settings) {
      for (const id in json.settings) {
        const setting = json.settings[id];

        // remap detailId & detailNumber properties of foundation conditions
        if (setting.type === "FOUNDATION_SETTINGS") {
          for (const hash of foundationHashes) {
            if (!setting[hash]) {
              continue;
            }

            const { foundationConditions } = setting[hash];

            for (const key in foundationConditions) {
              const foundationCondition = foundationConditions[key];
              const { detailId, detailNumber } = foundationCondition;

              if (detailId) {
                foundationCondition.drawingId = detailId;
                delete foundationCondition.detailId;
              }

              if (detailNumber) {
                foundationCondition.drawingNumber = detailNumber;
                delete foundationCondition.detailNumber;
              }
            }
          }
        }

        if (setting.type === "DETAIL_CATALOG_SETTINGS") {
          settingsSetIdsToDrawingCatalogId[setting.parentId] = id;
          setting.type = "DRAWING_CATALOG_SETTINGS";
          setting.name = "Drawing Catalog Settings";

          // convert details to drawings
          setting.drawings = {};
          for (const detailId in setting.details) {
            const detail = setting.details[detailId];
            setting.drawings[detailId] = {
              id: detailId,
              type: detail.type ?? "OTHER",
              scale: 1,
              name: detail.name ?? "New Drawing",
              size: detail.size ?? "INHERIT",
              drawingScale: detail.scale ?? "NTS",
              url: detail.url ?? STATIC_MISSING_DRAWING_IMG_PATH,
            };
          }
          delete setting.details;
        }
      }
    }

    let parentSettingsSetId: string | undefined = undefined;

    const settingsSetsToCheck = !Object.keys(settingsSetIdsToDrawingCatalogId).length
      ? json.settingsSets
      : settingsSetIdsToDrawingCatalogId;

    // Determine which settings set to care about
    for (const id in settingsSetsToCheck) {
      const settingsSet = json.settingsSets[id];
      if (!parentSettingsSetId || settingsSet.sortIndex < json.settingsSets[parentSettingsSetId].sortIndex) {
        parentSettingsSetId = id;
      }
    }

    json.drawingRefs = {};
    let drawingCatalogSettings;
    let drawingCatalogId;

    // Persist previous drawing catalog settings
    if (parentSettingsSetId && settingsSetIdsToDrawingCatalogId[parentSettingsSetId]) {
      drawingCatalogId = settingsSetIdsToDrawingCatalogId[parentSettingsSetId];
      drawingCatalogSettings = json.settings[drawingCatalogId];
    } else if (Object.keys(json.drawings).length) {
      drawingCatalogId = uid();
      drawingCatalogSettings = {
        id: drawingCatalogId,
        type: "DRAWING_CATALOG_SETTINGS",
        drawings: {},
        parentId: parentSettingsSetId,
        sortIndex: 0,
        entityType: "SETTINGS",
        name: "Drawing Catalog Settings",
      };
    }

    if (json.drawings) {
      for (const drawingId in json.drawings) {
        const oldDrawing = json.drawings[drawingId];

        const {
          id,
          name,
          scale = 1,
          scaleMode = "UNIFORM",
          parentId,
          prettyName,
          disabled,
          sortIndex,
          ...catalogProps
        } = oldDrawing;

        const newDrawingId = uid();

        drawingCatalogSettings.drawings[newDrawingId] = {
          ...catalogProps,
          name: name.trim() !== "" ? name : "Untitled Drawing",
          id: newDrawingId,
          type: "OTHER",
          drawingScale: "NTS",
          size: "INHERIT",
          scaleMode,
          scale,
          catalogId: drawingCatalogId,
        };

        json.drawingRefs[id] = {
          id,
          name,
          entityType: "DRAWING_REF",
          drawingId: newDrawingId,
          parentId,
          prettyName,
          disabled,
          sortIndex,
          scale,
        };
      }
      delete json.drawings;

      if (drawingCatalogSettings) {
        json.settings[drawingCatalogSettings.id] = drawingCatalogSettings;
      }

      return json;
    }
  },
};

export default migration_0_0_145;
